import React, { useState, useEffect } from "react";
import { FaTrash, FaUser, FaHistory, FaTimes } from "react-icons/fa";
import AdminSidebar from "../../components/admin/AdminSidebar";
import AdminHeader from "../../components/admin/AdminHeader";
import userService from "../../services/userService";
import paymentService from "../../services/paymentService";
import "../../styles/admin/ManageUsers.css";
import CustomModal from "../../components/CustomModal";

function ManageUsers() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchEmail, setSearchEmail] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [stats, setStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    completedProjects: 0,
    totalEarnings: 0,
  });
  const [paymentHistory, setPaymentHistory] = useState({
    amount: "",
    description: "",
    status: "Paid",
    date: new Date().toISOString().split('T')[0]
  });
  const [licenseData, setLicenseData] = useState({
    key: "",
    expiry: ""
  });
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [userPaymentHistory, setUserPaymentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Modal State
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
    onConfirm: null,
    confirmText: "OK",
    cancelText: "Cancel"
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    // Filter users based on email search
    if (searchEmail.trim() === "") {
      setFilteredUsers(users);
    } else {
      const searchTerm = searchEmail.toLowerCase().trim();
      const filtered = users.filter(user => {
        const userEmail = (user.email || "").toLowerCase().trim();
        return userEmail.includes(searchTerm);
      });
      setFilteredUsers(filtered);
    }
  }, [searchEmail, users]);

  const fetchUsers = async () => {
    try {
      const data = await userService.getAllUsers();
      if (data) {
        setUsers(data);
        setFilteredUsers(data); // Initialize filtered users with all users
      }
    } catch (error) {
      console.error("Failed to fetch users", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setPaymentAmount(user.currentPayment || 0);
    setStats({
      totalProjects: user.totalProjects || 0,
      activeProjects: user.activeProjects || 0,
      completedProjects: user.completedProjects || 0,
      totalEarnings: user.totalEarnings || 0,
    });
    setLicenseData({
      key: user.licenseKey || "",
      expiry: user.licenseExpiry || ""
    });
  };

  const showModal = (title, message, type = 'info', onConfirm = null, confirmText = 'OK', cancelText = 'Cancel') => {
    setModalState({
      isOpen: true,
      title,
      message,
      type,
      onConfirm: onConfirm ? () => { onConfirm(); setModalState(prev => ({ ...prev, isOpen: false })); } : null,
      confirmText,
      cancelText
    });
  };

  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  const handleUpdatePayment = async () => {
    if (!selectedUser) return;

    try {
      const userId = selectedUser.id || selectedUser._id;
      await userService.updateUserPayment(userId, Number(paymentAmount));
      showModal("Success", "Payment updated successfully!", "success");
      fetchUsers();
    } catch (error) {
      console.error("Failed to update payment", error);
      showModal("Error", "Failed to update payment.", "error");
    }
  };

  const handleUpdateStats = async () => {
    if (!selectedUser) return;

    try {
      const userId = selectedUser.id || selectedUser._id;
      await userService.updateUserStats(
        userId,
        Number(stats.totalProjects),
        Number(stats.activeProjects),
        Number(stats.completedProjects),
        Number(stats.totalEarnings)
      );
      showModal("Success", "Stats updated successfully!", "success");
      fetchUsers();
    } catch (error) {
      console.error("Failed to update stats", error);
      showModal("Error", "Failed to update stats.", "error");
    }
  };

  const handleUpdateLicense = async () => {
    if (!selectedUser) return;

    try {
      const userId = selectedUser.id || selectedUser._id;
      await userService.updateUserLicense(
        userId,
        licenseData.key,
        licenseData.expiry
      );
      showModal("Success", "License updated successfully!", "success");
      fetchUsers();
    } catch (error) {
      console.error("Failed to update license", error);
      showModal("Error", "Failed to update license.", "error");
    }
  };

  const handleAddPaymentHistory = async () => {
    if (!selectedUser || !paymentHistory.amount || !paymentHistory.description) {
      showModal("Input Required", "Please fill in all payment history fields", "info");
      return;
    }

    try {
      // Use the Firebase document ID, not _id
      const userId = selectedUser.id || selectedUser._id;

      await paymentService.createPayment({
        userId: userId,
        userName: selectedUser.name,
        amount: Number(paymentHistory.amount),
        status: paymentHistory.status,
        description: paymentHistory.description,
        date: paymentHistory.date
      });

      showModal("Success", "Payment history added successfully!", "success");
      setPaymentHistory({
        amount: "",
        description: "",
        status: "Paid",
        date: new Date().toISOString().split('T')[0]
      });
    } catch (error) {
      console.error("Failed to add payment history", error);
      console.error("Error details:", error.message);
      showModal("Error", `Failed to add payment history: ${error.message}`, "error");
    }
  };

  const handleClearDataClick = () => {
    if (!selectedUser) return;
    showModal(
      "Clear User Data",
      `Are you sure you want to clear all data for ${selectedUser.name}? This will delete their payment history and reset their stats. This action cannot be undone.`,
      "danger",
      confirmClearData,
      "Clear Data",
      "Cancel"
    );
  };

  const confirmClearData = async () => {
    if (!selectedUser) return;

    try {
      const userId = selectedUser.id || selectedUser._id;
      await userService.clearUserData(userId);
      showModal("Success", "User data cleared successfully!", "success");
      fetchUsers();
      // Reset local state
      setPaymentAmount("");
      setStats({
        totalProjects: 0,
        activeProjects: 0,
        completedProjects: 0,
        totalEarnings: 0,
      });
    } catch (error) {
      console.error("Failed to clear user data", error);
      showModal("Error", "Failed to clear user data.", "error");
    }
  };

  const handleViewProfile = () => {
    setShowProfileModal(true);
  };

  const handleViewHistory = async () => {
    if (!selectedUser) return;
    setShowHistoryModal(true);
    setLoadingHistory(true);
    try {
      const userId = selectedUser.id || selectedUser._id;
      const history = await paymentService.fetchMyPayments(userId);
      // Sort by date desc
      history.sort((a, b) => new Date(b.date) - new Date(a.date));
      setUserPaymentHistory(history);
    } catch (error) {
      console.error("Failed to fetch payment history", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const closeModals = () => {
    setShowProfileModal(false);
    setShowHistoryModal(false);
  };

  return (
    <div className="admin-dashboard">
      <AdminSidebar />

      <div className="admin-main">
        <AdminHeader title="Manage Users" />
        <p className="page-subtitle" style={{ padding: '0 2rem' }}>Update user payments, stats, and payment history</p>

        {loading ? (
          <p style={{ padding: '0 2rem' }}>Loading users...</p>
        ) : (
          <div className="manage-users-layout" style={{ padding: '0 2rem' }}>
            <div className="users-list">
              <h3>All Users ({users.length})</h3>

              {/* Email Search */}
              <div className="search-box" style={{ marginBottom: '1rem' }}>
                <input
                  type="email"
                  placeholder="Search by email..."
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    color: 'var(--text-main)',
                    fontSize: '0.9rem'
                  }}
                />
              </div>

              {filteredUsers.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '2rem' }}>
                  No users found
                </p>
              ) : (
                filteredUsers.map((user) => (
                  <div
                    key={user._id}
                    className={`user-item ${selectedUser?._id === user._id ? 'selected' : ''}`}
                    onClick={() => handleSelectUser(user)}
                  >
                    <div className="user-info">
                      <h4>{user.name}</h4>
                      <p>{user.email}</p>
                      <span className="user-role">{user.role}</span>
                    </div>
                    <div className="user-stats-preview">
                      <small>₹{user.totalEarnings?.toLocaleString() || 0}</small>
                    </div>
                  </div>
                ))
              )}
            </div>

            {selectedUser && (
              <div className="user-details">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                  <h3 style={{ margin: 0 }}>Edit User: {selectedUser.name}</h3>
                  <div className="user-actions" style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={handleViewProfile}
                      className="btn-icon"
                      style={{
                        background: 'rgba(26, 115, 232, 0.1)',
                        border: '1px solid var(--accent-blue)',
                        color: 'var(--accent-blue)',
                        cursor: 'pointer',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.9rem',
                        fontWeight: '600'
                      }}
                    >
                      <FaUser /> View Profile
                    </button>
                    <button
                      onClick={handleViewHistory}
                      className="btn-icon"
                      style={{
                        background: 'rgba(26, 115, 232, 0.1)',
                        border: '1px solid var(--accent-blue)',
                        color: 'var(--accent-blue)',
                        cursor: 'pointer',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.9rem',
                        fontWeight: '600'
                      }}
                    >
                      <FaHistory /> Payment History
                    </button>
                    <button
                      onClick={handleClearDataClick}
                      className="btn-icon delete"
                      style={{
                        background: 'rgba(255, 77, 79, 0.1)',
                        border: '1px solid #ff4d4f',
                        color: '#ff4d4f',
                        cursor: 'pointer',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.9rem',
                        fontWeight: '600'
                      }}
                      title="Clear User Data"
                    >
                      <FaTrash /> Clear Data
                    </button>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Current Payment</h4>
                  <div className="input-group">
                    <label>Monthly Payment (₹)</label>
                    <input
                      type="number"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      placeholder="Enter payment amount"
                    />
                    <button onClick={handleUpdatePayment} className="btn-primary">
                      Update Payment
                    </button>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>User Statistics</h4>
                  <div className="stats-grid">
                    <div className="input-group">
                      <label>Total Projects</label>
                      <input
                        type="number"
                        value={stats.totalProjects}
                        onChange={(e) => setStats({ ...stats, totalProjects: e.target.value })}
                      />
                    </div>
                    <div className="input-group">
                      <label>Active Projects</label>
                      <input
                        type="number"
                        value={stats.activeProjects}
                        onChange={(e) => setStats({ ...stats, activeProjects: e.target.value })}
                      />
                    </div>
                    <div className="input-group">
                      <label>Completed Projects</label>
                      <input
                        type="number"
                        value={stats.completedProjects}
                        onChange={(e) => setStats({ ...stats, completedProjects: e.target.value })}
                      />
                    </div>
                    <div className="input-group">
                      <label>Total Earnings (₹)</label>
                      <input
                        type="number"
                        value={stats.totalEarnings}
                        onChange={(e) => setStats({ ...stats, totalEarnings: e.target.value })}
                      />
                    </div>
                  </div>
                  <button onClick={handleUpdateStats} className="btn-primary">
                    Update Stats
                  </button>
                </div>

                <div className="detail-section">
                  <h4>License Management</h4>
                  <div className="input-group">
                    <label>License Key</label>
                    <input
                      type="text"
                      value={licenseData.key}
                      onChange={(e) => setLicenseData({ ...licenseData, key: e.target.value })}
                      placeholder="Enter license key"
                    />
                  </div>
                  <div className="input-group">
                    <label>License Expiry</label>
                    <input
                      type="date"
                      value={licenseData.expiry}
                      onChange={(e) => setLicenseData({ ...licenseData, expiry: e.target.value })}
                    />
                  </div>
                  <button onClick={handleUpdateLicense} className="btn-primary">
                    Update License
                  </button>
                </div>

                <div className="detail-section">
                  <h4>Add Payment History</h4>
                  <div className="payment-history-form">
                    <div className="input-group">
                      <label>Amount (₹)</label>
                      <input
                        type="number"
                        value={paymentHistory.amount}
                        onChange={(e) => setPaymentHistory({ ...paymentHistory, amount: e.target.value })}
                        placeholder="Enter amount"
                      />
                    </div>
                    <div className="input-group">
                      <label>Description</label>
                      <input
                        type="text"
                        value={paymentHistory.description}
                        onChange={(e) => setPaymentHistory({ ...paymentHistory, description: e.target.value })}
                        placeholder="e.g., November Salary"
                      />
                    </div>
                    <div className="input-group">
                      <label>Date</label>
                      <input
                        type="date"
                        value={paymentHistory.date}
                        onChange={(e) => setPaymentHistory({ ...paymentHistory, date: e.target.value })}
                      />
                    </div>
                    <div className="input-group">
                      <label>Status</label>
                      <select
                        value={paymentHistory.status}
                        onChange={(e) => setPaymentHistory({ ...paymentHistory, status: e.target.value })}
                      >
                        <option value="Paid">Paid</option>
                        <option value="Processing">Processing</option>
                        <option value="Pending">Pending</option>
                      </select>
                    </div>
                  </div>
                  <button onClick={handleAddPaymentHistory} className="btn-primary">
                    Add Payment History
                  </button>
                </div>
              </div>
            )}

            {!selectedUser && (
              <div className="no-selection">
                <p>Select a user to manage their details</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Profile Modal */}
      {showProfileModal && selectedUser && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="modal-content" style={{
            background: 'white', padding: '2rem', borderRadius: '12px', width: '90%', maxWidth: '500px',
            position: 'relative', maxHeight: '90vh', overflowY: 'auto'
          }}>
            <button onClick={closeModals} style={{
              position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer'
            }}>
              <FaTimes />
            </button>
            <h3 style={{ marginBottom: '1.5rem', borderBottom: '1px solid #eee', paddingBottom: '1rem' }}>User Profile</h3>

            <div className="profile-details-grid" style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>Full Name</label>
                <p style={{ margin: '0.2rem 0', fontSize: '1.1rem' }}>{selectedUser.name || 'N/A'}</p>
              </div>
              <div>
                <label style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>Email</label>
                <p style={{ margin: '0.2rem 0', fontSize: '1.1rem' }}>{selectedUser.email || 'N/A'}</p>
              </div>
              <div>
                <label style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>Phone</label>
                <p style={{ margin: '0.2rem 0', fontSize: '1.1rem' }}>{selectedUser.phone || 'N/A'}</p>
              </div>
              <div>
                <label style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>Location</label>
                <p style={{ margin: '0.2rem 0', fontSize: '1.1rem' }}>{selectedUser.location || 'N/A'}</p>
              </div>
              <div>
                <label style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>Company</label>
                <p style={{ margin: '0.2rem 0', fontSize: '1.1rem' }}>{selectedUser.companyName || 'N/A'}</p>
              </div>
              <div>
                <label style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>Website</label>
                <p style={{ margin: '0.2rem 0', fontSize: '1.1rem' }}>
                  {selectedUser.website ? <a href={selectedUser.website} target="_blank" rel="noopener noreferrer">{selectedUser.website}</a> : 'N/A'}
                </p>
              </div>
              <div>
                <label style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>Skills</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                  {selectedUser.skills && selectedUser.skills.length > 0 ? (
                    selectedUser.skills.map((skill, idx) => (
                      <span key={idx} style={{ background: '#f0f2f5', padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.9rem' }}>
                        {skill}
                      </span>
                    ))
                  ) : (
                    <p>No skills listed</p>
                  )}
                </div>
              </div>
            </div>
            <div>
              <label style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>KYC Details</label>
              <div style={{ marginTop: '0.5rem', display: 'grid', gap: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontWeight: '500', minWidth: '70px' }}>PayPal:</span>
                  <span>{selectedUser.paypalId || 'Not provided'}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontWeight: '500', minWidth: '70px' }}>UPI:</span>
                  <span>{selectedUser.upiId || 'Not provided'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment History Modal */}
      {
        showHistoryModal && selectedUser && (
          <div className="modal-overlay" style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <div className="modal-content" style={{
              background: 'white', padding: '2rem', borderRadius: '12px', width: '90%', maxWidth: '600px',
              position: 'relative', maxHeight: '90vh', overflowY: 'auto'
            }}>
              <button onClick={closeModals} style={{
                position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer'
              }}>
                <FaTimes />
              </button>
              <h3 style={{ marginBottom: '1.5rem', borderBottom: '1px solid #eee', paddingBottom: '1rem' }}>Payment History</h3>

              {loadingHistory ? (
                <p>Loading history...</p>
              ) : (
                <div className="history-list">
                  {userPaymentHistory.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#666' }}>No payment history found.</p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f8f9fa', textAlign: 'left' }}>
                          <th style={{ padding: '0.8rem', borderBottom: '1px solid #eee' }}>Date</th>
                          <th style={{ padding: '0.8rem', borderBottom: '1px solid #eee' }}>Description</th>
                          <th style={{ padding: '0.8rem', borderBottom: '1px solid #eee' }}>Amount</th>
                          <th style={{ padding: '0.8rem', borderBottom: '1px solid #eee' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userPaymentHistory.map((payment) => (
                          <tr key={payment._id} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: '0.8rem' }}>{payment.date}</td>
                            <td style={{ padding: '0.8rem' }}>{payment.description}</td>
                            <td style={{ padding: '0.8rem', fontWeight: '600' }}>₹{payment.amount?.toLocaleString()}</td>
                            <td style={{ padding: '0.8rem' }}>
                              <span style={{
                                padding: '0.2rem 0.6rem', borderRadius: '100px', fontSize: '0.85rem',
                                background: payment.status === 'Paid' ? '#e6f7ff' : '#fff7e6',
                                color: payment.status === 'Paid' ? '#1890ff' : '#fa8c16'
                              }}>
                                {payment.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          </div>
        )
      }

      {/* Custom Modal for Alerts and Confirmations */}
      <CustomModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        onConfirm={modalState.onConfirm}
        confirmText={modalState.confirmText}
        cancelText={modalState.cancelText}
      />
    </div >
  );
}

export default ManageUsers;